import type { Variable } from '@tamagui/core';
import { useTheme } from 'tamagui';

import { objectFromEntries } from '../@tamagui/themes/helpers';
import {
    accent as brand,
    accentDark as brandDark,
    dropletBlue,
    dropletBlueDark,
    fruitfulGreen,
    gray,
    grayDark,
    green,
    greenDark,
    leaf,
    lemon,
    marigold,
    marigoldDark,
    orange,
    orangeDark,
    orchidPink,
    orchidPinkDark,
    peach,
    peachDark,
    petal,
    red,
    redDark,
    sweetViolet,
    sweetVioletDark,
    tomatoRed,
    tomatoRedDark,
    wasabi,
    wasabiDark,
    water,
} from './colors';

export type ColorName =
    | 'brand'
    | 'dropletBlue'
    | 'gray'
    | 'green'
    | 'marigold'
    | 'orange'
    | 'orchidPink'
    | 'peach'
    | 'red'
    | 'sweetViolet'
    | 'tomatoRed'
    | 'wasabi';
/** All smoothie color primitives are CSS color strings (hex/rgba). */
type ColorTokenValue = string;
export type ColorPalette = Record<string, ColorTokenValue>;

interface LightColorPalettes {
    brand: typeof brand;
    dropletBlue: typeof dropletBlue;
    gray: typeof gray;
    green: typeof green;
    marigold: typeof marigold;
    orange: typeof orange;
    orchidPink: typeof orchidPink;
    peach: typeof peach;
    red: typeof red;
    sweetViolet: typeof sweetViolet;
    tomatoRed: typeof tomatoRed;
    wasabi: typeof wasabi;
}

interface DarkColorPalettes {
    brand: typeof brandDark;
    dropletBlue: typeof dropletBlueDark;
    gray: typeof grayDark;
    green: typeof greenDark;
    marigold: typeof marigoldDark;
    orange: typeof orangeDark;
    orchidPink: typeof orchidPinkDark;
    peach: typeof peachDark;
    red: typeof redDark;
    sweetViolet: typeof sweetVioletDark;
    tomatoRed: typeof tomatoRedDark;
    wasabi: typeof wasabiDark;
}

interface ThemeColorPalettes {
    light: LightColorPalettes;
    dark: DarkColorPalettes;
}
export { type ThemeColorPalettes as ColorTokens };

export const colorTokens: ThemeColorPalettes = {
    light: {
        brand,
        dropletBlue,
        gray,
        green,
        marigold,
        orange,
        orchidPink,
        peach,
        red,
        sweetViolet,
        tomatoRed,
        wasabi,
    },
    dark: {
        brand: brandDark,
        dropletBlue: dropletBlueDark,
        gray: grayDark,
        green: greenDark,
        marigold: marigoldDark,
        orange: orangeDark,
        orchidPink: orchidPinkDark,
        peach: peachDark,
        red: redDark,
        sweetViolet: sweetVioletDark,
        tomatoRed: tomatoRedDark,
        wasabi: wasabiDark,
    },
};

export const colorPrimitiveTokens = {
    ...brand,
    ...dropletBlue,
    fruitfulGreen,
    ...gray,
    ...green,
    ...leaf,
    ...lemon,
    ...marigold,
    ...orange,
    ...orchidPink,
    ...peach,
    ...petal,
    ...red,
    ...sweetViolet,
    ...tomatoRed,
    ...wasabi,
    ...water,
};

export const darkColorPrimitiveTokens = {
    ...brandDark,
    ...dropletBlueDark,
    fruitfulGreen,
    ...grayDark,
    ...greenDark,
    ...leaf,
    ...lemon,
    ...marigoldDark,
    ...orangeDark,
    ...orchidPinkDark,
    ...peachDark,
    ...petal,
    ...redDark,
    ...sweetVioletDark,
    ...tomatoRedDark,
    ...wasabiDark,
    ...water,
};

type PostfixedColorKeys<
    A extends ColorPalette,
    B extends string,
> = `${keyof A extends string ? keyof A : never}${B}`;

function postfixObjKeys<A extends ColorPalette, B extends string>(
    obj: A,
    postfix: B,
): Record<PostfixedColorKeys<A, B>, Variable<string> | string> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return objectFromEntries(
        Object.entries(obj).map(([k, v]) => [`${k}${postfix}`, v]),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;
}

export const themedColorTokens = {
    ...postfixObjKeys(colorPrimitiveTokens, 'Light'),
    ...postfixObjKeys(darkColorPrimitiveTokens, 'Dark'),
};

export type ColorTokenKey = `$${keyof typeof colorPrimitiveTokens}`;

export const useThemeColors = () => {
    const theme = useTheme();
    const getColor = (color: ColorTokenKey) => {
        if (theme[color] == null) {
            console.error(
                `Color not found in current tamagui theme: ${color}; Returning ${theme.color.get()}`,
            );
            return theme.color.get();
        }

        return theme[color].get();
    };

    const getPrimitiveColor = (
        color: ColorTokenKey,
        scheme: 'light' | 'dark' = 'light',
    ): string => {
        const key = color.slice(1) as keyof typeof colorPrimitiveTokens;
        const tokens =
            scheme === 'dark' ? darkColorPrimitiveTokens : colorPrimitiveTokens;
        return tokens[key];
    };

    return { getColor, getPrimitiveColor };
};
